/**
 * @file mouse-usb-ota.ts
 * @description Telink 鼠标 USB OTA 编解码。通道层单独处理 Report ID。
 *
 * 进入命令：维盛 AP V0104 第 9 页 §5 发送 `05 AA BB CC`。
 * 进入成功：维盛 2026-08-20 勘正为控制口 `04 AA BB CC 00 00 00 00 FF`（应用态 0x0106 起）。
 * 写 BIN：`ota_app/usb_Form.cs` `ota_write` / `crc16` / `openBin_button_Click`。
 * 中间 Input 不解析 opcode，只作背压；最终 status 匹配 `05 02 03 00 06 FF`。
 */

/** Telink 鼠标 USB OTA 的已验证传输参数。 */
export const TELINK_KM_USB_OTA_HID = {
  reportId: 0x05,
  /** 维盛确认：应用态 0x0106 起，进入成功 Input 走控制口 Report ID 0x04。 */
  enterSuccessReportId: 0x04,
  packetByteLength: 64,
  payloadByteLength: 63,
  recordsPerUsbReport: 3,
  recordByteLength: 20,
  firmwareBytesPerRecord: 16,
} as const

/** 保持鼠标 OTA 调用方的既有导出名称。 */
export const TELINK_MOUSE_USB_OTA_HID = TELINK_KM_USB_OTA_HID

const ENTER_MAGIC = [0xaa, 0xbb, 0xcc] as const
const ENTER_SUCCESS_STATUS = 0xff
const ENTER_SUCCESS_PREFIX_LENGTH = 8
const OPEN_BIN_SIZE_OFFSET = 0x18
const OPEN_BIN_VERSION_OFFSET = 0x02
const OPEN_BIN_TRAILER_PAD = 16
const EXE_WRAPPER_SIZE_OFFSET = 0x30
const EXE_WRAPPER_IMAGE_OFFSET = 0x100
const MAX_SOURCE_BIN_SIZE = 0x200000
const CRC16_POLY = 0xa001
const CRC16_INIT = 0xffff

export interface TelinkKmUsbOpenBin {
  size: number
  version: number
  crc: number
  padded: Uint8Array
  layout: 'open-bin' | 'exe-wrapper' | 'raw-file'
}

export type TelinkKmUsbOtaFramePhase = 'start' | 'data' | 'end'

export interface TelinkKmUsbOtaFrame {
  phase: TelinkKmUsbOtaFramePhase
  payload: Uint8Array
  percent: number
  otaIndex: number
}

/**
 * 组装不含 Report ID 的 63 字节进入 OTA payload：`AA BB CC` + 其余 0x00。
 * 加上通道层 Report ID 后整包 64 字节，对应 V0104 `Length must be 0x40`。
 */
export function createTelinkMouseEnterUsbOtaPayload(): Uint8Array {
  const payload = new Uint8Array(TELINK_MOUSE_USB_OTA_HID.payloadByteLength)
  payload.set(ENTER_MAGIC)
  return payload
}

/**
 * 判断是否为进入 OTA 成功回包。
 *
 * 维盛 2026-08-20：VT2 应用态 `0x0106` 起回 `04 AA BB CC 00 00 00 00 FF`。
 * V0104 PDF 写的 `05 … FF` 与该固件不符；Report ID `05` 的同内容包不能当成功。
 * 请求回显（末字节 0）不是成功。若缓冲仍带 Report ID `04`，则跳过首字节。
 */
export function isTelinkMouseEnterUsbOtaSuccess(data: DataView, reportId?: number): boolean {
  if (reportId !== undefined && reportId !== TELINK_MOUSE_USB_OTA_HID.enterSuccessReportId) {
    return false
  }
  if (matchesEnterUsbOtaSuccessAt(data, 0)) return true
  return (
    data.byteLength > 0 &&
    data.getUint8(0) === TELINK_MOUSE_USB_OTA_HID.enterSuccessReportId &&
    matchesEnterUsbOtaSuccessAt(data, 1)
  )
}

function matchesEnterUsbOtaSuccessAt(data: DataView, offset: number): boolean {
  if (data.byteLength < offset + ENTER_SUCCESS_PREFIX_LENGTH) return false
  return (
    data.getUint8(offset) === ENTER_MAGIC[0] &&
    data.getUint8(offset + 1) === ENTER_MAGIC[1] &&
    data.getUint8(offset + 2) === ENTER_MAGIC[2] &&
    data.getUint8(offset + 3) === 0x00 &&
    data.getUint8(offset + 4) === 0x00 &&
    data.getUint8(offset + 5) === 0x00 &&
    data.getUint8(offset + 6) === 0x00 &&
    data.getUint8(offset + 7) === ENTER_SUCCESS_STATUS
  )
}

function viewOf(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

function createFfPayload(): Uint8Array {
  return new Uint8Array(TELINK_MOUSE_USB_OTA_HID.payloadByteLength).fill(0xff)
}

/**
 * 原厂 `crc16`：初值 0xFFFF，多项式 0xA001，按位右移。
 */
export function createTelinkKmUsbOtaCrc16(data: Uint8Array): number {
  let crc = CRC16_INIT
  for (let index = 0; index < data.byteLength; index++) {
    let ds = data[index]!
    for (let bit = 0; bit < 8; bit++) {
      crc = ((crc >>> 1) ^ [0, CRC16_POLY][(crc ^ ds) & 1]!) & 0xffff
      ds >>>= 1
    }
  }
  return crc
}

/**
 * 解析 USB OTA 镜像，三种布局与 C# 一致、互不混用：
 * 1. `openBin_button_Click`：长度 `u32 LE @ 0x18`
 * 2. `load_param`：长度 `u32 BE @ 0x30`，镜像从 `0x100`
 * 3. `generate_ota_app.ReadBinFile1`：整文件就是镜像（encrypt 烧入档走这条）
 * 发送前均补 16 字节 0xFF。CRC 为 0 / 0xFFFFFFFF 则拒绝。不解密。
 */
export function parseTelinkKmUsbOpenBin(file: Uint8Array): TelinkKmUsbOpenBin {
  return parseOpenBinLayout(file) ?? parseExeWrapperLayout(file) ?? parseRawFileLayout(file)
}

function finishParsedBin(
  image: Uint8Array,
  size: number,
  version: number,
  crc: number,
  layout: TelinkKmUsbOpenBin['layout'],
): TelinkKmUsbOpenBin {
  if (crc === 0 || crc === 0xffffffff) {
    throw new Error('Firmware CRC is wrong，illegal firmware')
  }
  const padded = new Uint8Array(size + OPEN_BIN_TRAILER_PAD).fill(0xff)
  padded.set(image.subarray(0, size))
  return { size, version, crc, padded, layout }
}

function parseOpenBinLayout(file: Uint8Array): TelinkKmUsbOpenBin | undefined {
  const view = viewOf(file)
  if (file.byteLength < OPEN_BIN_SIZE_OFFSET + 4) return undefined
  const size = view.getUint32(OPEN_BIN_SIZE_OFFSET, true)
  if (!Number.isInteger(size) || size < 4 || size > file.byteLength) return undefined
  return finishParsedBin(
    file,
    size,
    view.getUint32(OPEN_BIN_VERSION_OFFSET, true),
    view.getUint32(size - 4, true),
    'open-bin',
  )
}

function parseExeWrapperLayout(file: Uint8Array): TelinkKmUsbOpenBin | undefined {
  if (file.byteLength < EXE_WRAPPER_IMAGE_OFFSET + 4) return undefined
  const view = viewOf(file)
  const size = view.getUint32(EXE_WRAPPER_SIZE_OFFSET, false)
  if (!Number.isInteger(size) || size < 4 || size >= MAX_SOURCE_BIN_SIZE) return undefined
  if (EXE_WRAPPER_IMAGE_OFFSET + size > file.byteLength) return undefined
  const image = file.subarray(EXE_WRAPPER_IMAGE_OFFSET, EXE_WRAPPER_IMAGE_OFFSET + size)
  return finishParsedBin(
    image,
    size,
    view.getUint32(OPEN_BIN_VERSION_OFFSET, true),
    viewOf(image).getUint32(size - 4, true),
    'exe-wrapper',
  )
}

function parseRawFileLayout(file: Uint8Array): TelinkKmUsbOpenBin {
  if (file.byteLength < 4 || file.byteLength >= MAX_SOURCE_BIN_SIZE) {
    throw new Error(`BIN 长度 ${file.byteLength} 超出 C# 允许范围（4..<0x200000）`)
  }
  const view = viewOf(file)
  return finishParsedBin(
    file,
    file.byteLength,
    file.byteLength >= 6 ? view.getUint32(OPEN_BIN_VERSION_OFFSET, true) : 0,
    view.getUint32(file.byteLength - 4, true),
    'raw-file',
  )
}

export function createTelinkKmUsbOtaStartPayload(): Uint8Array {
  const payload = createFfPayload()
  payload.set([0x02, 0x02, 0x00, 0x01, 0xff])
  return payload
}

export function createTelinkKmUsbOtaEndPayload(lastIndex: number): Uint8Array {
  const payload = createFfPayload()
  const complement = (0xffff - lastIndex + 1) & 0xffff
  payload[0] = 0x02
  payload[1] = 0x06
  payload[2] = 0x00
  payload[3] = 0x02
  payload[4] = 0xff
  payload[5] = lastIndex & 0xff
  payload[6] = (lastIndex >> 8) & 0xff
  payload[7] = complement & 0xff
  payload[8] = (complement >> 8) & 0xff
  return payload
}

function writeDataRecord(
  target: Uint8Array,
  offset: number,
  blockIndex: number,
  padded: Uint8Array,
): void {
  const firmwareOffset = blockIndex * TELINK_MOUSE_USB_OTA_HID.firmwareBytesPerRecord
  target[offset] = blockIndex & 0xff
  target[offset + 1] = (blockIndex >> 8) & 0xff
  const crcSource = new Uint8Array(18)
  crcSource[0] = target[offset]!
  crcSource[1] = target[offset + 1]!
  for (let index = 0; index < TELINK_MOUSE_USB_OTA_HID.firmwareBytesPerRecord; index++) {
    const byte = padded[firmwareOffset + index] ?? 0xff
    target[offset + 2 + index] = byte
    crcSource[2 + index] = byte
  }
  const crc = createTelinkKmUsbOtaCrc16(crcSource)
  target[offset + 18] = crc & 0xff
  target[offset + 19] = (crc >> 8) & 0xff
}

/**
 * 复制 C# `start_ota_flag==2` 分支：每帧最多 3 条 20 字节记录；
 * `ota_index * 16 >= source_bin_size + 16` 时撤回序号并改发结束帧。
 */
export function createTelinkKmUsbOtaDataOrEndPayload(
  bin: Pick<TelinkKmUsbOpenBin, 'size' | 'padded'>,
  otaIndex: number,
):
  | { type: 'data'; payload: Uint8Array; nextIndex: number }
  | { type: 'end'; payload: Uint8Array; lastIndex: number } {
  const sendbuf = new Uint8Array(60)
  let length = 0
  let index = otaIndex
  for (let col = 0; col < TELINK_MOUSE_USB_OTA_HID.recordsPerUsbReport; col++) {
    writeDataRecord(sendbuf, col * TELINK_MOUSE_USB_OTA_HID.recordByteLength, index, bin.padded)
    index += 1
    if (
      index * TELINK_MOUSE_USB_OTA_HID.firmwareBytesPerRecord >=
      bin.size + OPEN_BIN_TRAILER_PAD
    ) {
      index -= 1
      break
    }
    length =
      TELINK_MOUSE_USB_OTA_HID.recordByteLength * col + TELINK_MOUSE_USB_OTA_HID.recordByteLength
  }

  if (length === 0) {
    const lastIndex = index - 1
    return { type: 'end', payload: createTelinkKmUsbOtaEndPayload(lastIndex), lastIndex }
  }

  const payload = createFfPayload()
  payload[0] = 0x02
  payload[1] = length
  payload[2] = 0x00
  payload.set(sendbuf.subarray(0, length), 3)
  return { type: 'data', payload, nextIndex: index }
}

/**
 * 按 C# `ota_write` 顺序产出启动帧、数据帧和结束帧。
 */
export class TelinkKmUsbOtaHostSession {
  private started = false
  private finished = false
  private otaIndex = 0

  public constructor(private readonly bin: Pick<TelinkKmUsbOpenBin, 'size' | 'padded'>) {}

  public next(): TelinkKmUsbOtaFrame | null {
    if (this.finished) return null
    if (!this.started) {
      this.started = true
      return {
        phase: 'start',
        payload: createTelinkKmUsbOtaStartPayload(),
        percent: 0,
        otaIndex: 0,
      }
    }

    const frame = createTelinkKmUsbOtaDataOrEndPayload(this.bin, this.otaIndex)
    if (frame.type === 'end') {
      this.finished = true
      return { phase: 'end', payload: frame.payload, percent: 100, otaIndex: frame.lastIndex }
    }

    this.otaIndex = frame.nextIndex
    const percent = Math.min(100, Math.floor((this.otaIndex * 16 * 100) / this.bin.size))
    return { phase: 'data', payload: frame.payload, percent, otaIndex: this.otaIndex }
  }
}

/**
 * C# `Read_File_Data_Process_YKQ` 最终 status：前缀 `05 02 03 00 06 FF`，下一字节为 status。
 * WebHID 可能已剥离 Report ID。
 */
export function parseTelinkKmUsbOtaFinalStatus(
  data: DataView,
  reportId?: number,
): number | undefined {
  return (
    matchStatusPrefix(data, 0) ??
    matchStatusPrefix(data, 1) ??
    (reportId === TELINK_MOUSE_USB_OTA_HID.reportId ? matchStatusPrefix(data, 0) : undefined)
  )
}

function matchStatusPrefix(data: DataView, offset: number): number | undefined {
  if (matchesBytes(data, offset, [0x02, 0x03, 0x00, 0x06, 0xff]) && data.byteLength >= offset + 6) {
    return data.getUint8(offset + 5)
  }
  if (
    matchesBytes(data, offset, [TELINK_MOUSE_USB_OTA_HID.reportId, 0x02, 0x03, 0x00, 0x06, 0xff]) &&
    data.byteLength >= offset + 7
  ) {
    return data.getUint8(offset + 6)
  }
  return undefined
}

function matchesBytes(data: DataView, offset: number, expected: readonly number[]): boolean {
  if (data.byteLength < offset + expected.length) return false
  return expected.every((byte, index) => data.getUint8(offset + index) === byte)
}
